/* @flow */

import { slice, get, set } from 'lodash';

import { getStepByField, StepOrder } from '../utils/stepHelpers';
import { StepState, Steps } from '../utils/constants';

import { prevStep, nextStep } from '../selectors/design';
import { constants } from '../actions/design';

import type { StateType, ValidStatusType } from '../actions/progress';

// const log = require('bows')('Saga/Design');

const { NOT_STARTED, ERROR, COMPLETE, DISABLED } = StepState;

export type StatusType = { id: string, status: ValidStatusType };

type ActionType = {
  payload: {
    content?: string,
    field: string,
    message: Object,
    messageFor?: string,
  },
  type: string,
};

/**
 * Helper function to create a StateType[] of given ids and status
 *
 * @param {string[]} list
 * @param {ValidStatusType} status
 * @returns {StatusType[]}
 */
function updateStatus(list: Array<*>, status: ValidStatusType): {} {
  return list.reduce(
    (obj, id) => ({
      ...obj,
      [id]: status,
    }),
    {},
  );
}

/**
 * Returns a StatusType[] of steps before the given step.
 *
 * @param {string} step
 * @returns {StatusType[]}
 */
function getPrevSteps(step: string): any {
  const index = StepOrder.findIndex(id => id === step);
  return slice(StepOrder, 0, index);
}

/**
 * Returns a StatusType[] of steps after the given step.
 *
 * @param {string} step
 * @returns {StatusType[]}
 */
function getNextSteps(step: ?string): any {
  const index = StepOrder.findIndex(id => id === step);
  return slice(StepOrder, index);
}


export const initialState: StateType = {
  active: StepOrder[0],
  hasError: false,
  status: updateStatus(StepOrder, NOT_STARTED),
  info: {},
  stepProgress: {},
};

/**
 * StepProgress reducer
 *
 * @export
 * @param {StateType} [state=initialState]
 * @param {ActionType} action
 * @returns {StateType}
 */
export default function (state: StateType = initialState, action: ActionType): any {
  switch (action.type) {
    case constants.JUMP_STEP: {
      const newStep: any = action.payload;
      const prevSteps = getPrevSteps(newStep);
      const completed = updateStatus(prevSteps, COMPLETE);

      /**
       * Jumping a step should do the following:
       * Set current step
       * If step is not started and no error - set all previous steps as completed
       * if step is completed - do nothing
       */
      return {
        ...state,
        status: {
          ...state.status,
          ...completed,
        },
        active: newStep,
      };
    }

    case constants.STEP_FORWARD: {
      const next = nextStep(state.active);
      const prevSteps = getPrevSteps(next);
      const completed = updateStatus(prevSteps, COMPLETE);
      return {
        ...state,
        status: {
          ...state.status,
          ...completed,
        },
        active: next,
      };
    }

    case constants.CHANGE_VALUE: {
      const field = state.active;
      const prevSteps = getPrevSteps(field);
      const nextSteps = getNextSteps(field);
      const completed = updateStatus(prevSteps, COMPLETE);
      const notStarted = updateStatus(nextSteps, NOT_STARTED);

      return {
        ...state,
        status: {
          ...state.status,
          ...notStarted,
          ...completed,
          [field]: NOT_STARTED,
        },
      };
    }

    case constants.STEP_BACKWARD: {
      const next = prevStep(state.active);
      return {
        ...state,
        active: next,
      };
    }

    case constants.NEW_DESIGN:
    case constants.RECEIVE_DESIGN: {
      return {
        ...initialState,
      };
    }

    case constants.AUTO_COMPLETE: {
      // Autocomplete should jump the user to loss review
      const completed = updateStatus(StepOrder, COMPLETE);
      return {
        status: {
          ...completed,
        },
        active: Steps.STEP_ANALYSIS,
      };
    }

    case constants.VALIDATION_ERROR: {
      const { field, message } = action.payload;
      const errorStep = getStepByField(field);

      // set error on `field` and set the remaining steps to disabled.
      // return set(state, ['status', errorStep], ERROR);
      const nextSteps = getNextSteps(errorStep);
      const disabled = updateStatus(nextSteps, DISABLED);

      if (!errorStep) return state;

      return {
        ...state,
        info: set(state, ['info', errorStep], message),
        status: {
          ...state.status,
          ...disabled,
          [errorStep]: ERROR,
        },
      };
    }

    case constants.UPDATE: {
      const { content, message } = action.payload;

      const type = get(action, ['payload', 'type']) || get(message, ['type']);

      const fieldRemap = (str?: string) => {
        if (!str) return undefined;

        const remap = {
          SuitableInductors: 'IndPNsel',
          Pkgs_FET0: 'Pkgs_FET',
          //   t_delay_hson: 'T_delay_hson',
          //   t_delay_lson: 'T_delay_lson',
          //   etaest_hl: 'Etaest_hl',
          //   etaest_ll: 'Etaest_ll',
          //   DC_CI: 'DF_CI',
        };

        return remap[str] || str;
      };

      const messageFor = fieldRemap(action.payload.messageFor);

      const messageForStep = messageFor && getStepByField(messageFor);
      const field = messageForStep || state.active;
      const prevSteps = getPrevSteps(field);
      const nextSteps = getNextSteps(field);
      const completed = updateStatus(prevSteps, COMPLETE);

      // log(messageFor, messageForStep, prevSteps, nextSteps);

      if (type === 'SUCCESS') {
        const notStarted = updateStatus(nextSteps, NOT_STARTED);

        return {
          ...state,
          status: {
            ...state.status,
            ...notStarted,
            ...completed,
            [field]: NOT_STARTED,
          },
        };
      } else if (type === 'ERROR' || type === 'INFO') {
        /**
         * In the event of an error:
         *   Set all following steps to 'disabled'
         *   Set step where error occured to 'error'
         */
        const disabled = updateStatus(nextSteps, DISABLED);
        return {
          ...state,
          info: set(state, ['info', field], content),
          status: {
            ...state.status,
            ...disabled,
            [state.active]: NOT_STARTED, // Sometimes an error occurs else where - active != field
            [field]: ERROR,
          },
        };
      }

      return state;
    }

    case constants.STEP_RESET: {
      // We need to reset any stages after this one
      const next = nextStep(state.active);
      const nextSteps = getNextSteps(next);
      const notStarted = updateStatus(nextSteps, NOT_STARTED);
      return {
        ...state,
        status: {
          ...state.status,
          ...notStarted,
          [state.active]: NOT_STARTED,
        },
      };
    }

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/progress.js